import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { fetchAllPages } from "@/lib/supabasePaging";
import { sortTasks } from "@/lib/caseTasks";
import { subscribeWithResync, uniqueChannelName } from "@/lib/realtimeResync";
import { isMissingSchemaError, SCHEMA_PENDING_MESSAGE } from "@/lib/supabaseErrors";
import { MANAGER_TASK_SELECT, periodStartIso, type ManagerPeriod, type ManagerTask } from "@/lib/managerStats";

/** Délai d'anti-rebond du rechargement après un événement temps réel (rafale de coches). */
const REALTIME_RELOAD_DEBOUNCE_MS = 600;

/**
 * Tâches de l'onglet Manager, avec le dossier joint : toutes les tâches créées dans la période
 * + toutes les tâches encore en cours (quelle que soit leur date de création).
 * Rechargement complet (anti-rebond) à chaque changement de `case_tasks`, à la reconnexion et au retour sur l'onglet.
 */
export function useManagerTasks(period: ManagerPeriod, enabled: boolean) {
  const [tasks, setTasks] = useState<ManagerTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestVersion = useRef(0);

  const load = useCallback(async () => {
    const version = ++requestVersion.current;
    const now = Date.now();
    const since = periodStartIso(period, now);
    try {
      const rows = await fetchAllPages<ManagerTask>((from, to) => {
        let query = supabase.from("case_tasks").select(MANAGER_TASK_SELECT);
        if (since) query = query.or(`created_at.gte."${since}",and(completed_at.is.null,cancelled_at.is.null)`);
        // Le select est construit par concaténation : supabase-js ne peut pas en déduire le type des lignes.
        return query.order("created_at", { ascending: false }).order("id").range(from, to) as unknown as PromiseLike<{
          data: ManagerTask[] | null;
          error: unknown;
        }>;
      });
      if (version !== requestVersion.current) return;
      setTasks(
        sortTasks(rows.map((row) => ({ ...row, case: Array.isArray(row.case) ? (row.case[0] ?? null) : row.case }))) as ManagerTask[],
      );
      setError(null);
    } catch (err) {
      if (version !== requestVersion.current) return;
      console.error("[Manager] Chargement des tâches impossible :", err);
      setError(isMissingSchemaError(err) ? SCHEMA_PENDING_MESSAGE : "Impossible de charger les tâches.");
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    if (!enabled) return;
    setLoading(true);
    void load();

    let timer: ReturnType<typeof setTimeout> | null = null;
    const scheduleReload = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        void load();
      }, REALTIME_RELOAD_DEBOUNCE_MS);
    };
    const channel = supabase
      .channel(uniqueChannelName("manager-case-tasks"))
      .on("postgres_changes", { event: "*", schema: "public", table: "case_tasks" }, scheduleReload);
    const cleanup = subscribeWithResync(channel, () => void load());
    return () => {
      if (timer) clearTimeout(timer);
      requestVersion.current += 1;
      cleanup();
    };
  }, [enabled, load]);

  /** Remplace une ligne localement (ex. retour de la RPC d'annulation), en gardant le dossier joint. */
  const patchTask = useCallback((row: Partial<ManagerTask> & { id: string }) => {
    setTasks((prev) => prev.map((task) => (task.id === row.id ? { ...task, ...row, case: task.case } : task)));
  }, []);

  return { tasks, loading, error, refresh: load, patchTask };
}
